// Structural lane types keep this pure rule usable by the server without importing phone/DOM modules.
type EnquiryLane = 'answer' | 'waiting' | 'chase' | 'letgo' | 'later' | 'won' | 'closed' | 'sorting'
type EnquiryStage = 'new' | 'waiting' | 'second' | 'no_reply'

export type MessageHome = 'enquiries' | 'comingup' | 'late' | 'waiting'
export type EnquiryPill = 'yourturn' | 'waiting' | 'won' | 'dead'
export interface MessageGroup { home: MessageHome; label: string; quiet: boolean }

// One home per group (Liam, 1 Nov 2026). Enquiry waits stay in Enquiries, never in Waiting on them.
// These are group keys, as returned by rebook.ts stageKey, plus the enquiry lanes and the two standalone lists.
// The raw customer ladder's waiting/second/no_reply keys need an enquiryStage when used for an enquiry card.
export const MESSAGE_HOMES = {
  new: { home: 'enquiries', label: 'New enquiries', quiet: false },
  enqreplied: { home: 'enquiries', label: 'They replied', quiet: false },
  enqdetails: { home: 'enquiries', label: 'Details requested', quiet: false },
  enqquoted: { home: 'enquiries', label: 'Quote sent', quiet: false },
  oldquotes: { home: 'enquiries', label: 'Old quotes', quiet: true },
  answer: { home: 'enquiries', label: 'To answer', quiet: false },
  chase: { home: 'enquiries', label: 'To chase', quiet: false },
  sorting: { home: 'enquiries', label: 'Needs sorting', quiet: false },
  letgo: { home: 'enquiries', label: 'Gone quiet', quiet: true },
  later: { home: 'enquiries', label: 'Maybe later', quiet: true },
  won: { home: 'enquiries', label: 'Won', quiet: true },
  closed: { home: 'enquiries', label: 'Closed', quiet: true },
  moved: { home: 'comingup', label: 'Visit moved', quiet: false },
  upcoming: { home: 'comingup', label: 'Coming up', quiet: false },
  to_message: { home: 'comingup', label: 'First message', quiet: false },
  nodate: { home: 'comingup', label: 'No date yet', quiet: false },
  visit: { home: 'comingup', label: 'Needs a visit', quiet: true },
  bookagain: { home: 'comingup', label: 'Book again', quiet: false },
  replybooked: { home: 'comingup', label: 'Booked after replying', quiet: true },
  overdue: { home: 'late', label: 'Late', quiet: false },
  lapsed: { home: 'late', label: 'Lapsed', quiet: true },
  recanvass: { home: 'late', label: 'Recanvass', quiet: true },
  taken_off: { home: 'late', label: 'Taken off', quiet: true },
  stopped: { home: 'late', label: 'Stopped', quiet: true },
  awaiting: { home: 'waiting', label: 'Waiting on them', quiet: false },
  waiting: { home: 'waiting', label: 'Waiting for a reply', quiet: false },
  second: { home: 'waiting', label: 'Second message', quiet: false },
  no_reply: { home: 'waiting', label: 'No reply', quiet: false },
  datesoffered: { home: 'waiting', label: 'Dates offered', quiet: false },
  followup: { home: 'waiting', label: 'Ask again later', quiet: true },
  paused: { home: 'waiting', label: 'Paused', quiet: true },
  offers: { home: 'waiting', label: 'Offers of another job', quiet: false },
} as const satisfies Record<string, MessageGroup>

export type MessageStageKey = keyof typeof MESSAGE_HOMES

export function homeOf(stageKey: string): MessageHome | null {
  return Object.hasOwn(MESSAGE_HOMES, stageKey) ? MESSAGE_HOMES[stageKey as MessageStageKey].home : null
}

type EnquiryPillStage = EnquiryLane | EnquiryStage | 'enqreplied' | 'enqdetails' | 'enqquoted' | 'oldquotes'
const ENQUIRY_PILLS: Record<EnquiryPillStage, EnquiryPill | null> = {
  new: 'yourturn', answer: 'yourturn', chase: 'yourturn', sorting: 'yourturn', second: 'yourturn', no_reply: 'yourturn',
  enqreplied: 'yourturn', oldquotes: 'yourturn', enqdetails: 'waiting', enqquoted: 'waiting', waiting: 'waiting',
  won: 'won', letgo: null, later: null, closed: null,
}

// Won means confirmed, as enquiryLane defines it, even before a visit is booked (Liam, 1 Nov 2026).
export function pillOf(stage: EnquiryPillStage): EnquiryPill | null {
  return ENQUIRY_PILLS[stage]
}

export interface MessageDueItem {
  stageKey: string
  enquiryStage?: EnquiryLane | EnquiryStage
  nudgeDue?: boolean
  deferred?: boolean
  // Book again must have cleared payment-first rules; an offer must need an actual message, not Add/Stop offers.
  messageReady?: boolean
}

// Eligibility only, over already classified work for the business's day. No clock, reads, counts or screen wiring.
// A quiet list is reachable without becoming today's target (Liam, 1 Nov 2026: "everyone who needs a message today").
export function dueToday(item: MessageDueItem): boolean {
  if (item.deferred || !Object.hasOwn(MESSAGE_HOMES, item.stageKey)) return false
  if (MESSAGE_HOMES[item.stageKey as MessageStageKey].quiet) return false
  if (item.enquiryStage !== undefined) {
    const pill = pillOf(item.enquiryStage)
    return pill === 'yourturn' || (pill === 'waiting' && item.nudgeDue === true)
  }
  switch (item.stageKey) {
    case 'new': case 'enqreplied': case 'answer': case 'chase': case 'sorting':
    case 'moved': case 'upcoming': case 'to_message': case 'nodate': case 'overdue':
    case 'second': case 'no_reply': return true
    case 'enqdetails': case 'enqquoted': case 'waiting': case 'datesoffered': return item.nudgeDue === true
    case 'bookagain': case 'offers': return item.messageReady === true
    default: return false
  }
}
